import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import apiService from "../../services/apiService";
import "./Reports.css";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const TABS = [
  { key: "transactions", label: "Transactions" },
  { key: "audit", label: "Audit Trail" },
  { key: "salesorders", label: "Sales Orders" },
  { key: "deliveries", label: "Delivery per Client" },
  { key: "clientpurchases", label: "Client Purchases" },
  { key: "cancelreturn", label: "Cancel & Return" },
  { key: "poper", label: "PO per Supplier" },
  { key: "receivingpo", label: "Receiving PO" },
  { key: "expiry", label: "Expiry Items" },
];

const ACTIONS = [
  "",
  "OPENING_STOCK",
  "STOCK_OUT",
  "STOCK_RETURN",
  "STOCK_CANCEL_RETURN",
  "STOCK_TRANSFER",
  "EXPIRED",
];

const formatAction = (a) => {
  const x = String(a || "").toUpperCase();

  if (x === "STOCK_OUT") return "DELIVERED";
  if (x === "OPENING_STOCK") return "STOCK IN";
  if (x === "STOCK_CANCEL_RETURN") return "CANCEL_RETURN";
  if (x === "STOCK_RETURN") return "RETURN";

  return a;
};

const PO_STATUSES = ["", "Pending", "Partial", "Completed"];
const ORDER_TYPES = ["", "NORMAL", "DEMO", "FREE_GOODS", "REPLACEMENT"];
const DELIVERY_MODES = ["", "SINGLE", "STAGGERED"];
const SO_STATUS = ["", "PENDING", "ONGOING", "PARTIAL", "COMPLETE", "CANCEL"];
const DELIVERY_STATUS = [
  "",
  "PENDING",
  "PARTIAL",
  "COMPLETE",
  "CANCEL",
  "CANCELLED",
  "RETURNED",
];

const CLIENT_PURCHASE_GROUPS = [
  { value: "overall", label: "Overall" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annual", label: "Annual" },
];

const getRoleCode = () => {
  const u = JSON.parse(localStorage.getItem("user") || "{}");
  return String(
    u.RoleCode || u.roleCode || u.role || u.Role || ""
  ).toUpperCase();
};

const TAB_ACCESS = {
  MA: ["salesorders", "deliveries", "clientpurchases", "receivingpo"],
  SM: ["salesorders", "deliveries", "clientpurchases"],
  PMG: ["salesorders", "deliveries", "clientpurchases", "receivingpo"],
  EOP: ["salesorders", "deliveries", "clientpurchases"],

  I1: [
    "transactions",
    "audit",
    "salesorders",
    "deliveries",
    "clientpurchases",
    "cancelreturn",
    "receivingpo",
    "expiry",
  ],
  I2: [
    "transactions",
    "audit",
    "salesorders",
    "deliveries",
    "clientpurchases",
    "cancelreturn",
    "receivingpo",
    "expiry",
  ],
  WHM: [
    "transactions",
    "audit",
    "salesorders",
    "deliveries",
    "clientpurchases",
    "cancelreturn",
    "receivingpo",
    "expiry",
  ],
  LS: [
    "transactions",
    "audit",
    "deliveries",
    "clientpurchases",
    "cancelreturn",
    "receivingpo",
    "expiry",
  ],

  ACC: ["transactions", "deliveries", "clientpurchases", "salesorders"],
  PO: [
    "transactions",
    "deliveries",
    "clientpurchases",
    "cancelreturn",
    "receivingpo",
    "expiry",
    "poper",
  ],
  ADM: [
    "transactions",
    "audit",
    "salesorders",
    "deliveries",
    "clientpurchases",
    "cancelreturn",
    "poper",
    "receivingpo",
    "expiry",
  ],
  EXECOM: [
    "transactions",
    "audit",
    "salesorders",
    "deliveries",
    "clientpurchases",
    "cancelreturn",
    "poper",
    "receivingpo",
    "expiry",
  ],
};

const getAllowedTabs = (roleCode) =>
  TAB_ACCESS[roleCode] || ["salesorders", "deliveries", "clientpurchases"];

const canAccessTab = (roleCode, tabKey) =>
  getAllowedTabs(roleCode).includes(tabKey);

const safeText = (v) => (v === null || v === undefined ? "" : String(v));

const fmtDateOnly = (v) => {
  if (!v) return "";

  const s = String(v).slice(0, 10);
  const d = new Date(`${s}T00:00:00`);

  if (Number.isNaN(d.getTime())) return safeText(v);

  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const exportFileName = (tabKey) => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");

  return `report_${tabKey}_${yyyy}-${mm}-${dd}`;
};

const normalizeReportResponse = (resp) => {
  if (Array.isArray(resp)) {
    return {
      rows: resp,
      total: resp.length,
      serverPaged: false,
    };
  }

  if (resp && typeof resp === "object") {
    const rows = Array.isArray(resp.rows)
      ? resp.rows
      : Array.isArray(resp.data)
        ? resp.data
        : [];

    const total = Number(resp.total ?? resp.count ?? rows.length);
    const serverPaged = total > rows.length;

    return {
      rows,
      total,
      serverPaged,
    };
  }

  return {
    rows: [],
    total: 0,
    serverPaged: false,
  };
};

const formatUnitLabel = (unit, fallback = "units") => {
  const raw = String(unit || "").trim();
  if (!raw) return fallback;

  const u = raw.toUpperCase();

  if (u === "BOX") return "boxes";
  if (u === "BOXES") return "boxes";
  if (u === "PIECE") return "pcs";
  if (u === "PIECES") return "pcs";
  if (u === "PCS") return "pcs";
  if (u === "PC") return "pcs";
  if (u === "CANISTER") return "canisters";
  if (u === "CANISTERS") return "canisters";
  if (u === "VIAL") return "vials";
  if (u === "VIALS") return "vials";
  if (u === "BOTTLE") return "bottles";
  if (u === "BOTTLES") return "bottles";
  if (u === "PACK") return "packs";
  if (u === "PACKS") return "packs";
  if (u === "KIT") return "kits";
  if (u === "KITS") return "kits";

  return raw.toLowerCase();
};


const firstExistingValue = (obj, keys, fallback = "") => {
  for (const key of keys) {
    const value = obj?.[key];

    if (value !== null && value !== undefined && String(value).trim() !== "") {
      return value;
    }
  }

  return fallback;
};

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value;

  if (typeof value !== "string") return [];

  const raw = value.trim();
  if (!raw || !raw.startsWith("[")) return [];

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeDeliveredItemRows = (dn) => {
  const possibleJsonFields = [
    dn?.DeliveredItemsJson,
    dn?.DeliveredItemDetails,
    dn?.DeliveredItemDetailsJson,
    dn?.ItemsJson,
    dn?.Items,
    dn?.items,
  ];

  const jsonRows = possibleJsonFields
    .map(parseJsonArray)
    .find((list) => list.length > 0);

  if (jsonRows?.length) {
    return jsonRows.map((item, idx) => ({
      RowID: firstExistingValue(item, ["DNItemID", "DNDetailID", "ItemID"], idx),
      CatalogNo: firstExistingValue(item, ["CatalogNo", "catalogNo", "CatalogNumber", "catalogNumber"]),
      ItemName: firstExistingValue(item, ["ItemName", "itemName", "Description", "description"]),
      DeliveredQty: firstExistingValue(item, [
        "DeliveredQty",
        "DeliveredQuantity",
        "Quantity",
        "Qty",
        "qty",
        "TotalQty",
      ]),
      Unit: firstExistingValue(item, ["Unit", "unit", "ItemUnit", "ItemPiece", "UOM"]),
      LotNumber: firstExistingValue(item, ["LotNumber", "lotNumber", "LotNo", "lotNo"]),
      ExpiryDate: firstExistingValue(item, ["ExpiryDate", "expiryDate", "ExpirationDate"]),
    }));
  }

  const deliveredItemsText = safeText(dn?.DeliveredItems).trim();

  if (!deliveredItemsText) return [];

  return deliveredItemsText
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name, idx) => ({
      RowID: `text-${idx}`,
      CatalogNo: "",
      ItemName: name,
      DeliveredQty: "",
      Unit: "",
      LotNumber: "",
      ExpiryDate: "",
    }));
};

const formatQty = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return "-";
  }

  const n = Number(value);

  if (!Number.isFinite(n)) return safeText(value);

  return n.toLocaleString("en-PH", {
    maximumFractionDigits: 2,
  });
};

export default function Reports() {
  const [tab, setTab] = useState("transactions");
  const [loading, setLoading] = useState(false);

  const [rows, setRows] = useState([]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalRows, setTotalRows] = useState(0);
  const [serverPaged, setServerPaged] = useState(false);

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [action, setAction] = useState("");
  const [performedBy, setPerformedBy] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [catalogNo, setCatalogNo] = useState("");
  const [itemName, setItemName] = useState("");

  const [type, setType] = useState("");

  const [supplierId, setSupplierId] = useState("");
  const [poStatus, setPoStatus] = useState("");

  const [days, setDays] = useState(30);

  const [soStatus, setSoStatus] = useState("");
  const [deliveryStatus, setDeliveryStatus] = useState("");
  const [orderType, setOrderType] = useState("");
  const [deliveryMode, setDeliveryMode] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [areaCode, setAreaCode] = useState("");
  const [areaCodeOptions, setAreaCodeOptions] = useState([]);
  const [purchaseGroupBy, setPurchaseGroupBy] = useState("overall");

  const [viewMode, setViewMode] = useState("detailed");

  const [compact, setCompact] = useState(true);
  const [showCols, setShowCols] = useState(false);
  const [visibleCols, setVisibleCols] = useState([]);

  const [dnModalOpen, setDnModalOpen] = useState(false);
  const [dnModalCustomer, setDnModalCustomer] = useState(null);
  const [dnListLoading, setDnListLoading] = useState(false);
  const [dnList, setDnList] = useState([]);
  const [dnItemsModalOpen, setDnItemsModalOpen] = useState(false);
  const [selectedDN, setSelectedDN] = useState(null);
  const [dnItemSearch, setDnItemSearch] = useState("");

  const [clientItemsModalOpen, setClientItemsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientItemsLoading, setClientItemsLoading] = useState(false);
  const [clientItems, setClientItems] = useState([]);

  const pollTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  const POLL_MS = 30000;

  const roleCode = useMemo(() => getRoleCode(), []);

  const allowedTabKeys = useMemo(() => getAllowedTabs(roleCode), [roleCode]);

  const filteredTabs = useMemo(
    () => TABS.filter((t) => allowedTabKeys.includes(t.key)),
    [allowedTabKeys]
  );

  useEffect(() => {
    let alive = true;

    const loadAreaCodes = async () => {
      try {
        const customers = await apiService.getCustomers();
        const list = Array.isArray(customers) ? customers : [];

        const codes = Array.from(
          new Set(
            list
              .map((c) =>
                String(
                  c.Area_Manager_Code ||
                    c.AreaCode ||
                    c.areaCode ||
                    c.area_manager_code ||
                    ""
                ).trim()
              )
              .filter(Boolean)
          )
        ).sort((a, b) => a.localeCompare(b));

        if (alive) setAreaCodeOptions(codes);
      } catch (e) {
        console.error("Load area codes error:", e);
        if (alive) setAreaCodeOptions([]);
      }
    };

    loadAreaCodes();

    return () => {
      alive = false;
    };
  }, []);

  const supportsSummary = [
    "transactions",
    "audit",
    "cancelreturn",
    "receivingpo",
  ].includes(tab);

  const purchaseGroupLabel = useMemo(() => {
    return (
      CLIENT_PURCHASE_GROUPS.find((g) => g.value === purchaseGroupBy)?.label ||
      "Overall"
    );
  }, [purchaseGroupBy]);

  const clientItemCols = useMemo(() => {
    const base = [
      "CatalogNo",
      "ItemName",
      "Unit",
      "TotalPurchasedQty",
      "FirstPurchaseDate",
      "LastPurchaseDate",
    ];

    if (purchaseGroupBy === "overall") return base;

    return ["Period", ...base];
  }, [purchaseGroupBy]);

  const clearPoll = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!allowedTabKeys.includes(tab)) {
      setTab(allowedTabKeys[0] || "deliveries");
    }
  }, [allowedTabKeys, tab]);

  const defaultCols = useMemo(() => {
    const map = {};

    map.transactions = [
      "DateTime",
      "Action",
      "CatalogNo",
      "ItemName",
      "LotNumber",
      "ChangeQty",
      "WarehouseName",
      "PerformedBy",
    ];

    map.audit = [
      "DateTime",
      "PerformedBy",
      "Action",
      "ReferenceDisplay",
      "CatalogNo",
      "ItemName",
      "Barcode",
      "LotNumber",
      "ChangeQty",
      "WarehouseName",
      "FromWarehouse",
      "ToWarehouse",
      "ReturnReason",
    ];

    map.salesorders = [
      "SONumber",
      "SODate",
      "CustomerName",
      "AreaCode",
      "OrderType",
      "DeliveryMode",
      "DeliveryStatus",
      "SOStatus",
      "ItemLines",
      "TotalQty",
      "PreparedBy",
      "CreatedAt",
    ];

    map.deliveries = [
      "CustomerID",
      "CustomerName",
      "DeliveryCount",
      "DeliveredItems",
      "FirstDeliveryDate",
      "LastDeliveryDate",
    ];

    map.clientpurchases = [
      "CustomerID",
      "CustomerName",
      "AreaCode",
      "ItemCount",
      "TotalPurchasedQty",
      "FirstPurchaseDate",
      "LastPurchaseDate",
    ];

    map.cancelreturn = [
      "DateTime",
      "Action",
      "CustomerName",
      "SONumber",
      "DNNumber",
      "DNStatus",
      "CatalogNo",
      "ItemName",
      "Barcode",
      "LotNumber",
      "ChangeQty",
      "ReturnReason",
      "PerformedBy",
    ];

    map.poper = [
      "SupplierID",
      "SupplierName",
      "POCount",
      "FirstPODate",
      "LastPODate",
    ];

    map.receivingpo = [
      "PONumber",
      "SupplierName",
      "OrderDate",
      "RequestDeliveryDate",
      "POStatus",
      "CatalogNo",
      "ItemName",
      "OrderedQty",
      "ReceivedQty",
      "PendingQty",
      "ReceivedBy",
      "StockedQty",
      "StockedBy",
      "DetailStatus",
    ];

    map.expiry = [
      "CatalogNo",
      "ItemName",
      "LotNumber",
      "ExpiryDate",
      "AlertDate",
      "DaysToExpiry",
      "WarehouseName",
      "BoxCount",
      "TotalPieces",
      "LotStatus",
    ];

    return map;
  }, []);

  const summaryCols = useMemo(() => {
    const map = {};

    map.transactions = [
      "FirstDateTime",
      "LastDateTime",
      "Action",
      "CatalogNo",
      "ItemName",
      "LotNumber",
      "WarehouseName",
      "TotalQty",
      "TransactionCount",
      "BarcodeCount",
    ];

    map.audit = [
      "FirstDateTime",
      "LastDateTime",
      "PerformedBy",
      "Action",
      "ReferenceDisplay",
      "CatalogNo",
      "ItemName",
      "LotNumber",
      "WarehouseName",
      "FromWarehouse",
      "ToWarehouse",
      "TotalQty",
      "TransactionCount",
      "BarcodeCount",
    ];

    map.cancelreturn = [
      "FirstDateTime",
      "LastDateTime",
      "Action",
      "CustomerName",
      "SONumber",
      "DNNumber",
      "CatalogNo",
      "ItemName",
      "LotNumber",
      "TotalQty",
      "TransactionCount",
      "BarcodeCount",
    ];

    map.receivingpo = [
      "PONumber",
      "SupplierName",
      "OrderDate",
      "RequestDeliveryDate",
      "POStatus",
      "CatalogNo",
      "ItemName",
      "TotalOrderedQty",
      "TotalReceivedQty",
      "PendingQty",
      "TotalStockedQty",
      "PendingToStock",
      "ItemLineCount",
    ];

    return map;
  }, []);

  useEffect(() => {
    const canSummary = [
      "transactions",
      "audit",
      "cancelreturn",
      "receivingpo",
    ].includes(tab);

    if (viewMode === "summary" && canSummary) {
      setVisibleCols(summaryCols[tab] || defaultCols[tab] || []);
    } else {
      setVisibleCols(defaultCols[tab] || []);
    }
  }, [tab, viewMode, defaultCols, summaryCols]);

  useEffect(() => {
    if (
      !["transactions", "audit", "cancelreturn", "receivingpo"].includes(tab) &&
      viewMode === "summary"
    ) {
      setViewMode("detailed");
    }
  }, [tab, viewMode]);

  useEffect(() => {
    setPage(1);
  }, [tab]);

  useEffect(() => {
    setPage(1);
  }, [
    from,
    to,
    action,
    performedBy,
    warehouseId,
    catalogNo,
    itemName,
    type,
    supplierId,
    poStatus,
    days,
    soStatus,
    deliveryStatus,
    orderType,
    deliveryMode,
    customerId,
    areaCode,
    purchaseGroupBy,
    pageSize,
    viewMode,
  ]);

  const buildParams = useCallback(() => {
    const p = {};

    if (tab !== "expiry") {
      if (from) p.from = from;
      if (to) p.to = to;
    }

    if (tab === "transactions") {
      if (action) p.action = action;
      if (warehouseId) p.warehouseId = warehouseId;
      if (catalogNo) p.catalogNo = catalogNo.trim();
      if (itemName) p.itemName = itemName.trim();
      p.viewMode = viewMode;
    }

    if (tab === "deliveries") {
      if (catalogNo) p.catalogNo = catalogNo.trim();
      if (itemName) p.itemName = itemName.trim();
    }

    if (tab === "audit") {
      if (action) p.action = action;
      if (performedBy) p.performedBy = performedBy;
      if (warehouseId) p.warehouseId = warehouseId;
      p.viewMode = viewMode;
    }

    if (tab === "cancelreturn") {
      if (type) p.type = type;
      p.viewMode = viewMode;
    }

    if (tab === "poper" || tab === "receivingpo") {
      if (supplierId) p.supplierId = supplierId;
    }

    if (tab === "receivingpo") {
      if (poStatus) p.status = poStatus;
      p.viewMode = viewMode;
    }

    if (tab === "expiry") {
      p.days = days;
      if (warehouseId) p.warehouseId = warehouseId;
    }

    if (tab === "salesorders") {
      if (soStatus) p.soStatus = soStatus;
      if (deliveryStatus) p.deliveryStatus = deliveryStatus;
      if (orderType) p.orderType = orderType;
      if (deliveryMode) p.deliveryMode = deliveryMode;
      if (customerId) p.customerId = customerId;
      if (areaCode) p.areaCode = areaCode;
    }

    if (tab === "clientpurchases") {
      if (catalogNo) p.catalogNo = catalogNo.trim();
      if (soStatus) p.soStatus = soStatus;
      if (deliveryStatus) p.deliveryStatus = deliveryStatus;
      if (customerId) p.customerId = customerId;
      if (areaCode) p.areaCode = areaCode;
    }

    p.page = page;
    p.pageSize = pageSize;

    return p;
  }, [
    tab,
    from,
    to,
    action,
    warehouseId,
    catalogNo,
    itemName,
    viewMode,
    performedBy,
    type,
    supplierId,
    poStatus,
    days,
    soStatus,
    deliveryStatus,
    orderType,
    deliveryMode,
    customerId,
    areaCode,
    page,
    pageSize,
  ]);

  const buildAllParams = useCallback(() => {
    const p = buildParams();

    delete p.page;
    delete p.pageSize;

    p.exportAll = 1;

    return p;
  }, [buildParams]);

  const hasDateRange = useCallback(() => {
    if (tab === "expiry" || tab === "clientpurchases") return true;
    return Boolean(from && to);
  }, [tab, from, to]);

  const fetchReport = useCallback(async () => {
    if (!canAccessTab(roleCode, tab)) {
      setRows([]);
      setTotalRows(0);
      setServerPaged(false);
      return;
    }

    if (!hasDateRange()) {
      setRows([]);
      setTotalRows(0);
      setServerPaged(false);
      return;
    }

    setLoading(true);

    try {
      const params = buildParams();
      let resp = [];

      if (tab === "transactions") resp = await apiService.rptTransactions(params);
      if (tab === "audit") resp = await apiService.rptAuditTrail(params);
      if (tab === "salesorders") resp = await apiService.rptSalesOrders(params);
      if (tab === "deliveries") resp = await apiService.rptDeliveriesPerClient(params);
      if (tab === "clientpurchases") resp = await apiService.rptClientPurchaseClients(params);
      if (tab === "cancelreturn") resp = await apiService.rptCancelReturn(params);
      if (tab === "poper") resp = await apiService.rptPoPerSupplier(params);
      if (tab === "receivingpo") resp = await apiService.rptReceivingPO(params);
      if (tab === "expiry") resp = await apiService.rptExpiryItems(params);

      const norm = normalizeReportResponse(resp);

      setRows(norm.rows);
      setTotalRows(norm.total);
      setServerPaged(norm.serverPaged);
    } catch (e) {
      console.error("REPORT fetch error:", e);
      alert("Failed to load report.");
    } finally {
      setLoading(false);
    }
  }, [roleCode, tab, buildParams, hasDateRange]);

  const fetchAllReportRows = useCallback(async () => {
    if (!hasDateRange()) {
      alert("Please select From and To date first.");
      return [];
    }

    const params = buildAllParams();
    let resp = [];

    if (tab === "transactions") resp = await apiService.rptTransactions(params);
    if (tab === "audit") resp = await apiService.rptAuditTrail(params);
    if (tab === "salesorders") resp = await apiService.rptSalesOrders(params);
    if (tab === "deliveries") resp = await apiService.rptDeliveriesPerClient(params);
    if (tab === "clientpurchases") resp = await apiService.rptClientPurchaseClients(params);
    if (tab === "cancelreturn") resp = await apiService.rptCancelReturn(params);
    if (tab === "poper") resp = await apiService.rptPoPerSupplier(params);
    if (tab === "receivingpo") resp = await apiService.rptReceivingPO(params);
    if (tab === "expiry") resp = await apiService.rptExpiryItems(params);

    const norm = normalizeReportResponse(resp);
    return norm.rows || [];
  }, [tab, buildAllParams, hasDateRange]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      clearPoll();
    };
  }, [clearPoll]);

  const allowPolling = useMemo(() => {
    return (
      page === 1 &&
      !dnModalOpen &&
      !dnItemsModalOpen &&
      !clientItemsModalOpen &&
      !dnListLoading &&
      !clientItemsLoading &&
      hasDateRange()
    );
  }, [
    page,
    dnModalOpen,
    dnItemsModalOpen,
    clientItemsModalOpen,
    dnListLoading,
    clientItemsLoading,
    hasDateRange,
  ]);

  useEffect(() => {
    let cancelled = false;

    if (!allowPolling) {
      clearPoll();
      return undefined;
    }

    const tick = async () => {
      if (cancelled || !isMountedRef.current) return;

      if (loading) {
        pollTimerRef.current = setTimeout(tick, POLL_MS);
        return;
      }

      try {
        await fetchReport();
      } catch {
        // silent polling error
      } finally {
        if (!cancelled && isMountedRef.current) {
          pollTimerRef.current = setTimeout(tick, POLL_MS);
        }
      }
    };

    clearPoll();
    pollTimerRef.current = setTimeout(tick, POLL_MS);

    return () => {
      cancelled = true;
      clearPoll();
    };
  }, [allowPolling, loading, fetchReport, clearPoll]);

  const pageCount = useMemo(() => {
    const total = Number(totalRows || 0);
    const ps = Number(pageSize || 1);

    return Math.max(1, Math.ceil(total / ps));
  }, [totalRows, pageSize]);

  const displayRows = useMemo(() => {
    if (!rows?.length) return [];

    if (serverPaged) return rows;

    const start = (page - 1) * pageSize;
    const end = start + pageSize;

    return rows.slice(start, end);
  }, [rows, page, pageSize, serverPaged]);

  useEffect(() => {
    if (!serverPaged) {
      setTotalRows(Array.isArray(rows) ? rows.length : 0);
    }
  }, [rows, serverPaged]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const shownCols = useMemo(() => {
    const base = serverPaged ? rows : displayRows;

    if (!base?.length) return [];

    const cols = base?.[0] ? Object.keys(base[0]) : [];
    const preferred = visibleCols?.length ? visibleCols : cols;

    return preferred.filter((c) => cols.includes(c));
  }, [rows, displayRows, visibleCols, serverPaged]);

  const toggleCol = (col) => {
    setVisibleCols((prev) => {
      const set = new Set(prev);

      if (set.has(col)) {
        set.delete(col);
      } else {
        set.add(col);
      }

      return Array.from(set);
    });
  };

  const formatCellValue = (row, key) => {
    const value = row?.[key];
    const unitLabel = formatUnitLabel(row?.Unit, "units");

    if (key === "Action") return formatAction(value);

    if (key === "TotalQty") return `${safeText(value)} ${unitLabel}`;
    if (key === "TotalPurchasedQty") return safeText(value);
    if (key === "TotalAmount") {
      const n = Number(value || 0);
      return n.toLocaleString("en-PH", {
        style: "currency",
        currency: "PHP",
        minimumFractionDigits: 2,
      });
    }
    if (key === "TransactionCount") return `${safeText(value)} entries`;
    if (key === "BarcodeCount") return `${safeText(value)} barcodes`;
    if (key === "BoxCount") return `${safeText(value)} ${formatUnitLabel(row?.Unit, "boxes")}`;
    if (key === "TotalPieces") return `${safeText(value)} pcs`;
    if (key === "ItemLineCount") return `${safeText(value)} lines`;
    if (key === "TotalOrderedQty") return `${safeText(value)} ${unitLabel}`;
    if (key === "TotalReceivedQty") return `${safeText(value)} ${unitLabel}`;
    if (key === "TotalStockedQty") return `${safeText(value)} ${unitLabel}`;
    if (key === "OrderedQty") return `${safeText(value)} ${unitLabel}`;
    if (key === "ReceivedQty") return `${safeText(value)} ${unitLabel}`;
    if (key === "StockedQty") return `${safeText(value)} ${unitLabel}`;
    if (key === "PendingQty") return `${safeText(value)} ${unitLabel}`;
    if (key === "PendingToStock") return `${safeText(value)} ${unitLabel}`;
    if (key === "ChangeQty") return `${safeText(value)} ${unitLabel}`;

    if (key.toLowerCase().includes("date")) return fmtDateOnly(value);

    if (tab === "audit" && key === "Reference") {
      return safeText(
        row?.ReferenceDisplay ||
          row?.PONumber ||
          row?.DNNumber ||
          row?.SONumber ||
          row?.Reference
      );
    }

    return safeText(value);
  };

  const buildExportData = (sourceRows) => {
    const header = shownCols;
    const body = sourceRows.map((r) => header.map((k) => formatCellValue(r, k)));

    return {
      header,
      body,
    };
  };

  const getFilterLine = (modeLabel) => {
    return tab === "expiry"
      ? `Mode: ${modeLabel} | Days: ${days}${warehouseId ? ` | WarehouseID: ${warehouseId}` : ""}`
      : `Mode: ${modeLabel} | From: ${from || "—"}  To: ${to || "—"}` +
          (action ? ` | Action: ${action}` : "") +
          (performedBy ? ` | PerformedBy: ${performedBy}` : "") +
          (warehouseId ? ` | WarehouseID: ${warehouseId}` : "") +
          (catalogNo ? ` | CatalogNo: ${catalogNo}` : "") +
          (itemName ? ` | ItemName: ${itemName}` : "") +
          (supplierId ? ` | SupplierID: ${supplierId}` : "") +
          (poStatus ? ` | POStatus: ${poStatus}` : "") +
          (type ? ` | Type: ${type}` : "") +
          (soStatus ? ` | SOStatus: ${soStatus}` : "") +
          (deliveryStatus ? ` | DeliveryStatus: ${deliveryStatus}` : "") +
          (orderType ? ` | OrderType: ${orderType}` : "") +
          (deliveryMode ? ` | DeliveryMode: ${deliveryMode}` : "") +
          (customerId ? ` | CustomerID: ${customerId}` : "") +
          (areaCode ? ` | AreaCode: ${areaCode}` : "") +
          (tab === "clientpurchases" ? ` | Group By: ${purchaseGroupLabel}` : "") +
          (["transactions", "audit", "cancelreturn", "receivingpo"].includes(tab)
            ? ` | View: ${viewMode}`
            : "") +
          ` | PageSize: ${pageSize}`;
  };

  const exportPDF = async (mode = "page") => {
    const sourceRows = mode === "all" ? await fetchAllReportRows() : displayRows;

    if (!sourceRows.length) return alert("No data to export.");

    const { header, body } = buildExportData(sourceRows);
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    const tabLabel = TABS.find((t) => t.key === tab)?.label || tab;
    const modeLabel = mode === "all" ? "All Filtered Rows" : `Current Page (${page})`;

    doc.setFontSize(14);
    doc.text(`Reports - ${tabLabel}`, 40, 35);

    doc.setFontSize(9);
    doc.text(getFilterLine(modeLabel), 40, 52);

    autoTable(doc, {
      startY: 70,
      head: [header],
      body,
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [30, 30, 30],
      },
      margin: {
        left: 40,
        right: 40,
      },
    });

    doc.save(`${exportFileName(tab)}_${mode === "all" ? "all" : `p${page}`}.pdf`);
  };

  const exportExcel = async (mode = "page") => {
    const sourceRows = mode === "all" ? await fetchAllReportRows() : displayRows;

    if (!sourceRows.length) return alert("No data to export.");

    const { header, body } = buildExportData(sourceRows);
    const tabLabel = TABS.find((t) => t.key === tab)?.label || tab;
    const modeLabel = mode === "all" ? "All Filtered Rows" : `Current Page (${page})`;

    const wsData = [
      ["BIOCARE INVENTORY MANAGEMENT SYSTEM"],
      [`Report: ${tabLabel}`],
      [`Export Date: ${new Date().toLocaleString()}`],
      [getFilterLine(modeLabel)],
      [],
      header,
      ...body,
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    ws["!cols"] = header.map((h) => ({
      wch: Math.max(14, String(h).length + 4),
    }));

    ws["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: Math.max(header.length - 1, 0) },
      },
      {
        s: { r: 1, c: 0 },
        e: { r: 1, c: Math.max(header.length - 1, 0) },
      },
      {
        s: { r: 2, c: 0 },
        e: { r: 2, c: Math.max(header.length - 1, 0) },
      },
      {
        s: { r: 3, c: 0 },
        e: { r: 3, c: Math.max(header.length - 1, 0) },
      },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");

    const out = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `${exportFileName(tab)}_${mode === "all" ? "all" : `p${page}`}.xlsx`);
  };

  const exportClientItemsExcel = () => {
    if (!clientItems.length) return alert("No purchased items to export.");

    const header = clientItemCols;
    const body = clientItems.map((item) =>
      header.map((key) => {
        if (key === "FirstPurchaseDate" || key === "LastPurchaseDate") {
          return fmtDateOnly(item?.[key]);
        }

        return formatCellValue(item, key);
      })
    );

    const customerName = safeText(selectedClient?.CustomerName || "Client");
    const wsData = [
      ["BIOCARE INVENTORY MANAGEMENT SYSTEM"],
      ["Report: Purchased Items / Reagents"],
      [`Customer: ${customerName}`],
      [`Group By: ${purchaseGroupLabel}`],
      [`Export Date: ${new Date().toLocaleString()}`],
      [
        `Filters: ${from ? `From ${from}` : "All dates"}${
          to ? ` | To ${to}` : ""
        }${catalogNo ? ` | CatalogNo: ${catalogNo}` : ""}${
          soStatus ? ` | SOStatus: ${soStatus}` : ""
        }${deliveryStatus ? ` | DeliveryStatus: ${deliveryStatus}` : ""}`,
      ],
      [],
      header,
      ...body,
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    ws["!cols"] = header.map((h) => ({
      wch: Math.max(14, String(h).length + 4),
    }));

    ws["!merges"] = [0, 1, 2, 3, 4, 5].map((r) => ({
      s: { r, c: 0 },
      e: { r, c: Math.max(header.length - 1, 0) },
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Purchased Items");

    const out = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const cleanName = customerName
      .replace(/[^a-zA-Z0-9-_]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 60);

    saveAs(
      blob,
      `purchased_items_${cleanName || "client"}_${purchaseGroupBy}.xlsx`
    );
  };

  const expireExpiryRow = async (r) => {
    const ok = window.confirm(
      `Expire / OUT this lot?\n\nItem: ${r.ItemName}\nLot: ${r.LotNumber}\nWarehouse: ${r.WarehouseName}\n\nThis will mark all available BOXES as OUT and log EXPIRED transactions.`
    );

    if (!ok) return;

    try {
      setLoading(true);

      await apiService.expireLot({
        itemId: r.ItemID,
        ItemID: r.ItemID,
        lotNumber: r.LotNumber,
        LotNumber: r.LotNumber,
        warehouseId: r.WarehouseID,
        WarehouseID: r.WarehouseID,
      });

      await fetchReport();
    } catch (e) {
      console.error("Expire lot error:", e);
      alert(e?.message || "Failed to expire lot.");
    } finally {
      setLoading(false);
    }
  };

  const openCustomerDNs = async (customerRow) => {
    setDnModalOpen(true);
    setDnModalCustomer(customerRow);
    setDnList([]);

    setDnListLoading(true);

    try {
      const list = await apiService.rptDeliveriesClientDNs({
        customerId: customerRow.CustomerID,
        from,
        to,
        catalogNo: catalogNo?.trim(),
        itemName: itemName?.trim(),
      });

      setDnList(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("Load customer DNs error:", e);
      alert("Failed to load DNs for this customer.");
    } finally {
      setDnListLoading(false);
    }
  };

  const openDNItemsModal = (dnRow) => {
    setSelectedDN(dnRow);
    setDnItemSearch("");
    setDnItemsModalOpen(true);
  };

  const closeDNItemsModal = () => {
    setDnItemsModalOpen(false);
    setSelectedDN(null);
    setDnItemSearch("");
  };

  const selectedDNItems = useMemo(() => {
    return normalizeDeliveredItemRows(selectedDN);
  }, [selectedDN]);

  const filteredSelectedDNItems = useMemo(() => {
    const q = dnItemSearch.trim().toLowerCase();

    if (!q) return selectedDNItems;

    return selectedDNItems.filter((item) => {
      const haystack = [
        item.CatalogNo,
        item.ItemName,
        item.DeliveredQty,
        item.Unit,
        item.LotNumber,
        item.ExpiryDate,
      ]
        .map(safeText)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [selectedDNItems, dnItemSearch]);

  const selectedDNTotalQty = useMemo(() => {
    return selectedDNItems.reduce((sum, item) => {
      const qty = Number(item.DeliveredQty || 0);
      return Number.isFinite(qty) ? sum + qty : sum;
    }, 0);
  }, [selectedDNItems]);

  const openClientItems = async (clientRow) => {
    setClientItemsModalOpen(true);
    setSelectedClient(clientRow);
    setClientItems([]);
    setClientItemsLoading(true);

    try {
      const list = await apiService.rptClientPurchaseClientItems({
        customerId: clientRow.CustomerID,
        from,
        to,
        catalogNo: catalogNo?.trim(),
        soStatus,
        deliveryStatus,
        groupBy: purchaseGroupBy,
      });

      setClientItems(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("Load client purchased items error:", e);
      alert("Failed to load purchased items for this client.");
    } finally {
      setClientItemsLoading(false);
    }
  };

  const closeClientItemsModal = () => {
    setClientItemsModalOpen(false);
    setSelectedClient(null);
    setClientItems([]);
  };

  const closeDnModal = () => {
    setDnModalOpen(false);
    setDnModalCustomer(null);
    setDnList([]);
    setDnItemsModalOpen(false);
    setSelectedDN(null);
    setDnItemSearch("");
  };

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(pageCount, p + 1));

  const pageButtons = useMemo(() => {
    const maxBtns = 7;
    const total = pageCount;

    if (total <= maxBtns) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const out = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(total, page + 2);

    out.push(1);

    if (start > 2) out.push("...");

    for (let i = start; i <= end; i++) {
      if (i !== 1 && i !== total) out.push(i);
    }

    if (end < total - 1) out.push("...");

    out.push(total);

    return out.filter((v, i, a) => a.indexOf(v) === i);
  }, [page, pageCount]);

  return (
    <div className="reports-page">
      <div className="reports-header">
        <div>
          <h2 className="reports-title">Reports</h2>
          <div className="reports-sub">
            Filter, run, and export operational reports.
          </div>

          <div className="muted" style={{ marginTop: 4 }}>
            Role: <strong>{roleCode || "UNKNOWN"}</strong>
          </div>
        </div>

        <div className="reports-actions">
          <button className="btn" onClick={() => setCompact((s) => !s)}>
            {compact ? "Comfort View" : "Compact View"}
          </button>

          <button
            className="btn"
            onClick={() => setShowCols((s) => !s)}
            disabled={!displayRows.length}
          >
            Columns
          </button>

          <button
            className="btn"
            onClick={() => exportExcel("page")}
            disabled={!displayRows.length || !hasDateRange()}
          >
            Excel Page
          </button>

          <button
            className="btn"
            onClick={() => exportExcel("all")}
            disabled={(!rows.length && !displayRows.length) || !hasDateRange()}
          >
            Excel All
          </button>

          <button
            className="btn"
            onClick={() => exportPDF("page")}
            disabled={!displayRows.length || !hasDateRange()}
          >
            PDF Page
          </button>

          <button
            className="btn"
            onClick={() => exportPDF("all")}
            disabled={(!rows.length && !displayRows.length) || !hasDateRange()}
          >
            PDF All
          </button>

          <button
            className="btn primary"
            onClick={() => {
              setPage(1);
              fetchReport();
            }}
            disabled={loading || !hasDateRange()}
          >
            {loading ? "Loading..." : "Run Report"}
          </button>
        </div>
      </div>

      <div className="reports-tabs">
        {filteredTabs.map((t) => (
          <button
            key={t.key}
            className={`tab-btn ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="reports-filters">
        {tab !== "expiry" && (
          <>
            <div className="field">
              <label>{tab === "clientpurchases" ? "From (optional)" : "From"}</label>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>

            <div className="field">
              <label>{tab === "clientpurchases" ? "To (optional)" : "To"}</label>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
          </>
        )}

        {(tab === "transactions" || tab === "audit") && (
          <div className="field">
            <label>Action</label>
            <select value={action} onChange={(e) => setAction(e.target.value)}>
              {ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {a || "All"}
                </option>
              ))}
            </select>
          </div>
        )}

        {tab === "audit" && (
          <div className="field">
            <label>Performed By</label>
            <input
              value={performedBy}
              onChange={(e) => setPerformedBy(e.target.value)}
              placeholder="e.g. admin"
            />
          </div>
        )}

        {tab === "cancelreturn" && (
          <div className="field">
            <label>Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">All</option>
              <option value="RETURN">RETURN</option>
              <option value="CANCEL_RETURN">CANCEL_RETURN</option>
            </select>
          </div>
        )}

        {(tab === "poper" || tab === "receivingpo") && (
          <div className="field">
            <label>Supplier ID</label>
            <input
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
              placeholder="e.g. 1"
            />
          </div>
        )}

        {tab === "receivingpo" && (
          <div className="field">
            <label>PO Status</label>
            <select value={poStatus} onChange={(e) => setPoStatus(e.target.value)}>
              {PO_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s || "All"}
                </option>
              ))}
            </select>
          </div>
        )}

        {(tab === "transactions" || tab === "audit" || tab === "expiry") && (
          <div className="field">
            <label>Warehouse ID</label>
            <input
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
              placeholder="e.g. 1"
            />
          </div>
        )}

        {(tab === "transactions" || tab === "clientpurchases" || tab === "deliveries") && (
          <div className="field">
            <label>Catalog No</label>
            <input
              value={catalogNo}
              onChange={(e) => setCatalogNo(e.target.value)}
              placeholder="e.g. CHSL-0250"
            />
          </div>
        )}

        {(tab === "transactions" || tab === "deliveries") && (
          <div className="field">
            <label>Item Name</label>
            <input
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              placeholder="Search item name..."
            />
          </div>
        )}

        {tab === "expiry" && (
          <div className="field">
            <label>Days</label>
            <input
              type="number"
              min={1}
              value={days}
              onChange={(e) => setDays(Number(e.target.value || 0))}
              placeholder="30"
            />
          </div>
        )}

        {tab === "salesorders" && (
          <>
            <div className="field">
              <label>SO Status</label>
              <select value={soStatus} onChange={(e) => setSoStatus(e.target.value)}>
                {SO_STATUS.map((s) => (
                  <option key={s} value={s}>
                    {s || "All"}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>Delivery Status</label>
              <select
                value={deliveryStatus}
                onChange={(e) => setDeliveryStatus(e.target.value)}
              >
                {DELIVERY_STATUS.map((s) => (
                  <option key={s} value={s}>
                    {s || "All"}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>Order Type</label>
              <select value={orderType} onChange={(e) => setOrderType(e.target.value)}>
                {ORDER_TYPES.map((s) => (
                  <option key={s} value={s}>
                    {s || "All"}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>Delivery Mode</label>
              <select
                value={deliveryMode}
                onChange={(e) => setDeliveryMode(e.target.value)}
              >
                {DELIVERY_MODES.map((s) => (
                  <option key={s} value={s}>
                    {s || "All"}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>Customer ID</label>
              <input
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                placeholder="e.g. 10"
              />
            </div>

            <div className="field">
              <label>Area Code</label>
              <select value={areaCode} onChange={(e) => setAreaCode(e.target.value)}>
                <option value="">All</option>
                {areaCodeOptions.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {tab === "clientpurchases" && (
          <>
            <div className="field">
              <label>SO Status</label>
              <select value={soStatus} onChange={(e) => setSoStatus(e.target.value)}>
                {SO_STATUS.map((s) => (
                  <option key={s} value={s}>
                    {s || "All"}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>Delivery Status</label>
              <select
                value={deliveryStatus}
                onChange={(e) => setDeliveryStatus(e.target.value)}
              >
                {DELIVERY_STATUS.map((s) => (
                  <option key={s} value={s}>
                    {s || "All"}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>Group By</label>
              <select
                value={purchaseGroupBy}
                onChange={(e) => setPurchaseGroupBy(e.target.value)}
              >
                {CLIENT_PURCHASE_GROUPS.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>Customer ID</label>
              <input
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                placeholder="e.g. 10"
              />
            </div>

            <div className="field">
              <label>Area Code</label>
              <select value={areaCode} onChange={(e) => setAreaCode(e.target.value)}>
                <option value="">All</option>
                {areaCodeOptions.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {supportsSummary && (
          <div className="field">
            <label>View</label>
            <select value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
              <option value="detailed">Detailed</option>
              <option value="summary">Summary</option>
            </select>
          </div>
        )}

        <div className="field" style={{ marginLeft: "auto" }}>
          <label>Page Size</label>
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            {[15, 25, 50, 100, 200].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      {showCols && displayRows.length > 0 && (
        <div className="reports-columns">
          <div className="cols-head">
            <strong>Visible Columns</strong>
            <span className="muted">
              ({visibleCols.length}/
              {displayRows?.[0] ? Object.keys(displayRows[0]).length : 0})
            </span>
          </div>

          <div className="cols-grid">
            {(displayRows?.[0] ? Object.keys(displayRows[0]) : []).map((c) => (
              <label key={c} className="col-chip">
                <input
                  type="checkbox"
                  checked={visibleCols.includes(c)}
                  onChange={() => toggleCol(c)}
                />
                <span>{c}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className={`reports-tableWrap ${compact ? "compact" : ""}`}>
        <div
          className="tableMeta"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div className="badge">Rows: {totalRows}</div>

            <div className="badge ghost">
              Showing: {displayRows.length} (Page {page}/{pageCount})
            </div>

            {supportsSummary && (
              <div className={`badge ${viewMode === "summary" ? "success" : ""}`}>
                Mode: {viewMode === "summary" ? "Summary (Grouped)" : "Detailed (Raw Logs)"}
              </div>
            )}

            {serverPaged && <div className="badge">Server Paging</div>}
            {!serverPaged && <div className="badge ghost">Client Paging</div>}
            {loading && <div className="badge ghost">Fetching…</div>}

            {!["expiry", "clientpurchases"].includes(tab) && (!from || !to) && (
              <div className="badge danger">
                Select From and To date first
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "wrap",
            }}
          >
            <button className="btn sm" onClick={goPrev} disabled={loading || page <= 1}>
              Prev
            </button>

            {pageButtons.map((p, i) =>
              p === "..." ? (
                <span
                  key={`dots-${i}`}
                  className="muted"
                  style={{ padding: "0 6px" }}
                >
                  …
                </span>
              ) : (
                <button
                  key={p}
                  className={`btn sm ${Number(p) === page ? "primary" : ""}`}
                  onClick={() => setPage(Number(p))}
                  disabled={loading}
                >
                  {p}
                </button>
              )
            )}

            <button
              className="btn sm"
              onClick={goNext}
              disabled={loading || page >= pageCount}
            >
              Next
            </button>
          </div>
        </div>

        {!displayRows.length ? (
          <div className="empty">
            <div className="emptyTitle">No data</div>
            <div className="emptySub">
              {!["expiry", "clientpurchases"].includes(tab) && (!from || !to)
                ? "Please select From and To date first."
                : "Adjust filters, then click “Run Report”."}
            </div>
          </div>
        ) : (
          <div className="tableScroll">
            <table className="rptTable">
              <thead>
                <tr>
                  {shownCols.map((k) => (
                    <th key={k}>{k}</th>
                  ))}

                  {tab === "deliveries" && <th>DNs</th>}
                  {tab === "clientpurchases" && <th>Items</th>}
                  {tab === "expiry" && <th>Action</th>}
                </tr>
              </thead>

              <tbody>
                {displayRows.map((r, idx) => (
                  <tr key={idx}>
                    {shownCols.map((k) => (
                      <td
                        key={k}
                        style={
                          k === "DeliveredItems"
                            ? {
                                minWidth: 300,
                                maxWidth: 520,
                                whiteSpace: "normal",
                                lineHeight: 1.4,
                              }
                            : undefined
                        }
                      >
                        {formatCellValue(r, k)}
                      </td>
                    ))}

                    {tab === "deliveries" && (
                      <td>
                        <button className="btn sm" onClick={() => openCustomerDNs(r)}>
                          Show DNs
                        </button>
                      </td>
                    )}

                    {tab === "clientpurchases" && (
                      <td>
                        <button className="btn sm" onClick={() => openClientItems(r)}>
                          Show Items
                        </button>
                      </td>
                    )}

                    {tab === "expiry" && (
                      <td>
                        {String(r?.LotStatus || "").toUpperCase() === "ALREADY OUT" ? (
                          <span className="badge alreadyOut">ALREADY OUT</span>
                        ) : (
                          <button
                            className="btn sm danger"
                            onClick={() => expireExpiryRow(r)}
                            disabled={loading}
                          >
                            OUT (EXPIRED)
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {clientItemsModalOpen && (
        <div className="modalOverlay" onClick={closeClientItemsModal}>
          <div className="modalCard" onClick={(e) => e.stopPropagation()}>
            <div className="modalHead">
              <div>
                <div className="modalTitle">Purchased Items / Reagents</div>
                <div className="muted">
                  Customer: <strong>{selectedClient?.CustomerName}</strong>{" "}
                  ID: {selectedClient?.CustomerID} | Group By: <strong>{purchaseGroupLabel}</strong>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className="btn"
                  onClick={exportClientItemsExcel}
                  disabled={clientItemsLoading || !clientItems.length}
                >
                  Export Excel
                </button>

                <button className="btn" onClick={closeClientItemsModal}>
                  Close
                </button>
              </div>
            </div>

            {clientItemsLoading ? (
              <div className="empty" style={{ padding: 16 }}>
                Loading purchased items…
              </div>
            ) : !clientItems.length ? (
              <div className="empty" style={{ padding: 16 }}>
                No purchased items found for this client.
              </div>
            ) : (
              <div className="tableScroll" style={{ maxHeight: 520 }}>
                <table className="rptTable">
                  <thead>
                    <tr>
                      {clientItemCols.map((col) => (
                        <th key={col}>{col}</th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {clientItems.map((item, idx) => (
                      <tr
                        key={`${item.Period || "overall"}-${
                          item.ItemID || idx
                        }-${item.CatalogNo || idx}`}
                      >
                        {clientItemCols.map((col) => (
                          <td key={col}>
                            {col === "FirstPurchaseDate" || col === "LastPurchaseDate"
                              ? fmtDateOnly(item?.[col])
                              : formatCellValue(item, col)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {dnModalOpen && (
        <div className="modalOverlay" onClick={closeDnModal}>
          <div
            className="modalCard dn-items-modal"
            style={{ width: "min(1100px, 96vw)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modalHead">
              <div>
                <div className="modalTitle">Delivery Notes</div>
                <div className="muted">
                  Customer: <strong>{dnModalCustomer?.CustomerName}</strong>{" "}
                  ID: {dnModalCustomer?.CustomerID}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn" onClick={closeDnModal}>
                  Close
                </button>
              </div>
            </div>

            {dnListLoading ? (
              <div className="empty" style={{ padding: 16 }}>
                Loading DNs…
              </div>
            ) : !dnList.length ? (
              <div className="empty" style={{ padding: 16 }}>
                No DNs found for this customer.
              </div>
            ) : (
              <div className="tableScroll" style={{ maxHeight: 520 }}>
                <table className="rptTable">
                  <thead>
                    <tr>
                      <th>DN Number</th>
                      <th>DN Date</th>
                      <th>SO Number</th>
                      <th>Item Count</th>
                      <th>Status</th>
                      <th>Items</th>
                    </tr>
                  </thead>

                  <tbody>
                    {dnList.map((d) => {
                      const dnItems = normalizeDeliveredItemRows(d);

                      return (
                        <tr key={d.DNID}>
                          <td>{d.DNNumber}</td>
                          <td>{fmtDateOnly(d.DNDate)}</td>
                          <td>{d.SONumber}</td>
                          <td>{dnItems.length || "-"}</td>
                          <td>{d.DeliveryStatus || d.DNStatus || ""}</td>
                          <td>
                            <button
                              className="btn sm"
                              onClick={() => openDNItemsModal(d)}
                            >
                              View Items
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {dnItemsModalOpen && (
        <div className="modalOverlay" onClick={closeDNItemsModal}>
          <div
            className="modalCard dn-items-detail-modal"
            style={{ width: "min(1050px, 96vw)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modalHead">
              <div>
                <div className="modalTitle">Delivered Items</div>
                <div className="muted">
                  DN: <strong>{selectedDN?.DNNumber}</strong> | SO: {selectedDN?.SONumber} | Date:{" "}
                  {fmtDateOnly(selectedDN?.DNDate)}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn" onClick={closeDNItemsModal}>
                  Back to DNs
                </button>
              </div>
            </div>

            <div className="reports-filters" style={{ marginTop: 12 }}>
              <div className="field" style={{ minWidth: 320 }}>
                <label>Search Item</label>
                <input
                  value={dnItemSearch}
                  onChange={(e) => setDnItemSearch(e.target.value)}
                  placeholder="Search catalog no, item name, lot no..."
                  autoFocus
                />
              </div>

              <div className="badge ghost" style={{ alignSelf: "end", marginBottom: 2 }}>
                Items: {filteredSelectedDNItems.length}/{selectedDNItems.length}
              </div>

              <div className="badge success" style={{ alignSelf: "end", marginBottom: 2 }}>
                Total Qty: {selectedDNTotalQty ? formatQty(selectedDNTotalQty) : "-"}
              </div>
            </div>

            {!selectedDNItems.length ? (
              <div className="empty" style={{ padding: 16 }}>
                No item details found for this DN.
                <div className="emptySub">
                  Add DeliveredItemsJson in the backend response to show item quantity per DN.
                </div>
              </div>
            ) : !filteredSelectedDNItems.length ? (
              <div className="empty" style={{ padding: 16 }}>
                No matching item found.
              </div>
            ) : (
              <div className="tableScroll" style={{ maxHeight: 520 }}>
                <table className="rptTable">
                  <thead>
                    <tr>
                      <th>Catalog No</th>
                      <th>Item Name</th>
                      <th>Qty</th>
                      <th>Unit</th>
                      <th>Lot No</th>
                      <th>Expiry Date</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredSelectedDNItems.map((item, idx) => (
                      <tr key={`${item.RowID}-${idx}`}>
                        <td>{item.CatalogNo || "-"}</td>
                        <td className="delivered-items-cell">{item.ItemName || "-"}</td>
                        <td>{formatQty(item.DeliveredQty)}</td>
                        <td>{item.Unit || "-"}</td>
                        <td>{item.LotNumber || "-"}</td>
                        <td>{fmtDateOnly(item.ExpiryDate) || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
